const selectors = {
  trigger: '[data-blog-dropdown-trigger]',
  panel: '[data-blog-dropdown-panel]',
  label: '[data-blog-dropdown-label]',
  option: '[data-category-link]',
};

/**
 * Custom category dropdown for the blog listing on mobile.
 *
 * Selecting an option does not navigate or submit - it hands the chosen URL to
 * the `onSelect` callback so the section can load the category over AJAX.
 */
export default class BlogCategoryDropdown {
  /**
   * @param {HTMLElement} root
   * @param {(url: string, link: HTMLAnchorElement) => void} onSelect
   */
  constructor(root, onSelect) {
    this.root = root;
    this.onSelect = onSelect;

    this.trigger = root.querySelector(selectors.trigger);
    this.panel = root.querySelector(selectors.panel);
    this.label = root.querySelector(selectors.label);

    if (!this.trigger || !this.panel) {
      return;
    }

    this.onTriggerClick = this.toggle.bind(this);
    this.onKeyUp = this.onRootKeyUp.bind(this);
    this.onFocusOut = this.onRootFocusOut.bind(this);
    this.onPanelClick = this.onOptionClick.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.root.addEventListener('keyup', this.onKeyUp);
    this.root.addEventListener('focusout', this.onFocusOut);
    this.panel.addEventListener('click', this.onPanelClick);
  }

  get isOpen() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.removeAttribute('hidden');
  }

  close() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('hidden', '');
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  onRootKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE' || !this.isOpen) {
      return;
    }

    this.close();
    event.stopPropagation();
    this.trigger.focus();
  }

  onRootFocusOut(event) {
    if (!this.root.contains(event.relatedTarget)) {
      this.close();
    }
  }

  onOptionClick(event) {
    const link = event.target.closest(selectors.option);

    if (!link) {
      return;
    }

    event.preventDefault();
    this.close();
    this.trigger.focus();

    if (this.label) {
      this.label.textContent =
        link.dataset.blogLabel || link.textContent.trim();
    }

    if (this.onSelect) {
      this.onSelect(link.dataset.blogUrl || link.getAttribute('href'), link);
    }
  }

  destroy() {
    if (!this.trigger || !this.panel) {
      return;
    }

    this.trigger.removeEventListener('click', this.onTriggerClick);
    this.root.removeEventListener('keyup', this.onKeyUp);
    this.root.removeEventListener('focusout', this.onFocusOut);
    this.panel.removeEventListener('click', this.onPanelClick);
  }
}
